import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Login.css';

function Login() {
  const [step, setStep] = useState('email'); // 'email' or 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const { sendCode, verifyCode, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/generator';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await sendCode(email);
      setMessage('Check your email for a verification code');
      setStep('code');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await verifyCode(email, code);
      // Auth context will update, useEffect will redirect
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setCode('');
    setError('');
    setMessage('');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <h1>🔐 Instructor Login</h1>
        <p className="login-subtitle">Access the playlist generator and library</p>

        {error && (
          <div className="login-error">
            ⚠️ {error}
          </div>
        )}

        {message && (
          <div className="login-message">
            ✉️ {message}
          </div>
        )}

        {step === 'email' && (
          <form onSubmit={handleSendCode} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoFocus
                disabled={loading}
              />
            </div>
            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? 'Sending...' : 'Send Login Code'}
            </button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={handleVerifyCode} className="login-form">
            <div className="form-group">
              <label htmlFor="code">Verification Code</label>
              <input
                type="text"
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
                disabled={loading}
              />
              <span className="form-hint">Enter the 6-digit code sent to {email}</span>
            </div>
            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
            <button 
              type="button" 
              className="btn-back" 
              onClick={handleBackToEmail}
              disabled={loading}
            >
              ← Use different email
            </button>
          </form>
        )}

        <div className="login-footer">
          <p>Only authorized instructors can access protected features.</p>
        </div>
      </div>
    </div>
  );
}

export default Login;
